import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon, ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
    fetchFieldIntelligence,
    fetchFieldDrilldown,
    IntelligenceMetrics,
} from '../services/advisoryApi';
import { handleApiError } from '../utils/api';

/**
 * Management field intelligence.
 *
 * Every figure is a real, tenant-scoped count from the server. A metric the
 * server could not compute arrives as null and is shown as "—", never as a
 * fabricated zero.
 */
const METRIC_LABELS: Array<{ key: keyof IntelligenceMetrics; label: string; tone?: string }> = [
    { key: 'farmsMonitored', label: 'Farms monitored' },
    { key: 'fieldsMonitored', label: 'Fields monitored' },
    { key: 'highRiskFields', label: 'High-risk fields', tone: 'text-red-600' },
    { key: 'fieldsNeedingReview', label: 'Fields needing review', tone: 'text-amber-600' },
    { key: 'openRecommendations', label: 'Open recommendations' },
    { key: 'recommendationsWaitingForAgronomist', label: 'Waiting for agronomist', tone: 'text-amber-600' },
    { key: 'overdueFieldTasks', label: 'Overdue field tasks', tone: 'text-red-600' },
    { key: 'openAgronomicIssues', label: 'Open agronomic issues' },
    { key: 'weatherSensitiveOperations', label: 'Weather-sensitive operations' },
    { key: 'completedRecommendationActions', label: 'Completed recommendation actions', tone: 'text-emerald-600' },
    { key: 'harvestReadyFields', label: 'Harvest-ready fields' },
];

const FieldIntelligencePage: React.FC = () => {
    const [metrics, setMetrics] = useState<IntelligenceMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({ crop: '', region: '', farm_id: '' });
    const [drillField, setDrillField] = useState('');
    const [drill, setDrill] = useState<Record<string, any> | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params: Record<string, string> = {};
            if (filters.crop) params.crop = filters.crop;
            if (filters.region) params.region = filters.region;
            if (filters.farm_id) params.farm_id = filters.farm_id;
            const data = await fetchFieldIntelligence(params);
            setMetrics(data.metrics);
        } catch (err) {
            setError(handleApiError(err));
            setMetrics(null);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const openDrilldown = async () => {
        const id = Number(drillField);
        if (!id) return;
        try {
            setDrill(await fetchFieldDrilldown(id));
        } catch (err) {
            setError(handleApiError(err));
            setDrill(null);
        }
    };

    const value = (v: number | null) => (v === null || v === undefined ? '—' : v);

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <ChartBarIcon className="h-7 w-7 text-slate-700" />
                    <div>
                        <h1 className="text-xl font-bold text-slate-800">Field Intelligence</h1>
                        <p className="text-sm text-slate-500">Operational view of agronomy across the organisation.</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <input className="border rounded-md px-3 py-1.5 text-sm" placeholder="Crop" value={filters.crop} onChange={(e) => setFilters((f) => ({ ...f, crop: e.target.value }))} />
                    <input className="border rounded-md px-3 py-1.5 text-sm" placeholder="Region" value={filters.region} onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value }))} />
                    <input className="border rounded-md px-3 py-1.5 text-sm" placeholder="Farm id" value={filters.farm_id} onChange={(e) => setFilters((f) => ({ ...f, farm_id: e.target.value }))} />
                    <button onClick={load} className="inline-flex items-center gap-1 rounded-md bg-slate-800 text-white px-3 py-1.5 text-sm">
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-md bg-red-50 text-red-800 px-4 py-2 text-sm flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-4 w-4" /> {error}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {METRIC_LABELS.map(({ key, label, tone }) => (
                    <div key={key} className="rounded-lg border bg-white p-4">
                        <div className={`text-2xl font-bold ${tone || 'text-slate-800'}`}>
                            {loading && !metrics ? '…' : value(metrics ? metrics[key] : null)}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{label}</div>
                    </div>
                ))}
            </div>

            <div className="rounded-lg border bg-white p-4">
                <h2 className="font-semibold text-slate-800">Drill down</h2>
                <p className="text-xs text-slate-500">
                    Organisation → farm → field → observation → recommendation → assignment → report.
                </p>
                <div className="mt-3 flex items-center gap-2">
                    <input
                        className="border rounded-md px-3 py-1.5 text-sm"
                        placeholder="Field id"
                        value={drillField}
                        onChange={(e) => setDrillField(e.target.value)}
                    />
                    <button onClick={openDrilldown} className="rounded-md border px-3 py-1.5 text-sm">Open</button>
                </div>

                {drill && (
                    <div className="mt-4 space-y-3 text-sm">
                        <div>
                            <div className="font-medium text-slate-700">{drill.field?.name || `Field ${drill.field?.id}`}</div>
                            <div className="text-xs text-slate-500">
                                {drill.field?.crop_type || 'crop unknown'} · {drill.field?.farmer_name || 'unknown farmer'}
                            </div>
                        </div>
                        <DrillSection title="Observations" rows={drill.observations} summary={(o) => `${o.observation_date || ''} · ${o.health_status || ''}`} />
                        <DrillSection title="Recommendations" rows={drill.recommendations} summary={(r) => `#${r.id} · ${r.status} · ${r.risk_level}`} />
                        <DrillSection title="Assignments" rows={drill.assignments} summary={(a) => `${a.assignment_code || a.id} · ${a.status}`} />
                        <DrillSection title="Reports" rows={drill.reports} summary={(rep) => `#${rep.id} · ${rep.status || ''}`} />
                    </div>
                )}
            </div>
        </div>
    );
};

const DrillSection: React.FC<{ title: string; rows: Record<string, any>[]; summary: (row: Record<string, any>) => string }> = ({ title, rows, summary }) => (
    <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title} ({rows?.length || 0})</div>
        {(rows || []).length === 0 ? (
            <div className="text-xs text-slate-400">None</div>
        ) : (
            <ul className="list-disc list-inside text-slate-700">
                {(rows || []).map((row, i) => <li key={row.id ?? i}>{summary(row)}</li>)}
            </ul>
        )}
    </div>
);

export default FieldIntelligencePage;
